import { useWindowSize } from "@kamae-apps/shared/Hooks/useWindowSize"
import { SCOPE, scopeHas } from "@kamae-apps/shared/Types/Scope"
import { isLargeScreen } from "@kamae-apps/shared/utils"
import { useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useUser } from "../Contexts/User/UserContext"
import { getCompanyPlan } from "../utils"
import config from "../variable"

// Intercom is injected on `window` by its loader snippet, so it must be read at
// call time rather than captured once.
function callIntercom(command: string, settings?: Record<string, unknown>) {
  ;(window as unknown as { Intercom?: (command: string, settings?: Record<string, unknown>) => void }).Intercom?.(
    command,
    settings
  )
}

// Boots Intercom on mount, then updates it with the user information for company
// admins (or shuts it down otherwise) once the user is loaded. Reads the shared
// UserContext, replacing old/hooks/useIntercom for the App root.
export function useIntercomSetup(isUserLoaded: boolean) {
  const { user } = useUser()
  const { i18n } = useTranslation()
  const size = useWindowSize()

  const instantiateIntercom = useCallback(() => {
    callIntercom("boot", {
      api_base: "https://api-iam.intercom.io",
      app_id: config.intercomKey,
      custom_launcher_selector: "#intercom-launcher",
      language_override: i18n.language,
    })
  }, [i18n.language])

  const updateIntercomWithUserInformation = useCallback(
    (hideLauncher: boolean) => {
      callIntercom("update", {
        user_id: user.id,
        user_hash: user.intercom_hash,
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        language_override: i18n.language,
        company: {
          company_id: user.company.id,
          name: user.company.name,
          size: user.company.total_user,
          plan: getCompanyPlan(user.company.status),
          partner_id: user.company.partner_id ?? null,
          partner_name: user.company.partner_name ?? null,
        },
        hide_default_launcher: hideLauncher,
      })
    },
    [user, i18n.language]
  )

  const shutdownIntercom = useCallback(() => {
    callIntercom("shutdown")
  }, [])

  useEffect(() => {
    instantiateIntercom()
  }, [instantiateIntercom])

  useEffect(() => {
    if (isUserLoaded && typeof user.scope !== "undefined") {
      if (scopeHas(user.scope, SCOPE.COMPANY_ADMIN)) {
        updateIntercomWithUserInformation(!isLargeScreen(size))
      } else {
        shutdownIntercom()
      }
    }
  }, [shutdownIntercom, updateIntercomWithUserInformation, isUserLoaded, user.scope, size])
}
